/* eslint-disable no-shadow, no-param-reassign */
import axios from "axios";
import swal from "sweetalert2";

const state = () => ({
  locales: [],
  terms: {},
  manufacturers: [],
  selectedLocaleId: null,
  lastSelectedLocale: null,
  selectedManufacturer: null,
  loadingManufacturers: false,
  salesContactName: null,
  salesContactEmail: null,
  salesContactPhone: null,
  submitting: false,
  errors: [],
});

const getters = {
  getLocales(state) {
    return state.locales;
  },

  getErrors(state) {
    return state.errors;
  },

  getManufacturers(state) {
    return state.manufacturers;
  },

  getLoadingManufacturers(state) {
    return state.loadingManufacturers;
  },

  getSalesContactName(state) {
    return state.salesContactName;
  },

  getSalesContactEmail(state) {
    return state.salesContactEmail;
  },

  getSalesContactPhone(state) {
    return state.salesContactPhone;
  },

  getManufacturerPayload(state) {
    const data = state.manufacturers.find(
      (m) => m.Id === state.selectedManufacturer
    );

    let terms = null;

    if (data.TermRef) {
      terms = (({ Id, Name }) => ({ value: Id, name: Name }))(
        state.terms[state.selectedLocaleId].find(
          (term) => term.Id === data.TermRef
        )
      );
    }

    return {
      locale_id: state.selectedLocaleId,
      inside_sales_contact_name: state.salesContactName,
      inside_sales_contact_email: state.salesContactEmail,
      inside_sales_contact_phone: state.salesContactPhone,
      qbo_id: data.Id ?? null,
      name: data.CompanyName ?? null,
      email: data.PrimaryEmailAddr.Address ?? null,
      phone: data.PrimaryPhone.FreeFormNumber ?? null,
      website_url: data.WebAddr.URI ?? null,
      solar_id: data.AcctNum ?? null,
      terms,
    };
  },

  getSubmitting(state) {
    return state.submitting;
  },
};

const actions = {
  DISMISS_ERROR({ commit, state }, error) {
    const { errors } = state;
    commit(
      "SET_ERRORS",
      errors.filter((err) => err !== error)
    );
  },

  FETCH_TERMS({ commit, state }) {
    const locale = state.selectedLocaleId;
    if (state.terms[locale] === undefined) {
      axios
        .get("/quickbooks/resource", {
          params: {
            locale,
            resource: "Term",
          },
        })
        .then((res) => {
          commit("SET_TERMS", { localeId: locale, value: res.data });
        })
        .catch((e) => {
          const { errors } = state;
          errors.push("Could not retrieve available manufacturer terms.");
          commit("SET_ERRORS", errors);
        });
    }
  },

  FETCH_MANUFACTURERS({ commit, state }) {
    if (state.selectedLocaleId === state.lastSelectedLocale) {
      return;
    }

    commit("SET_LOADING_MANUFACTURERS", true);
    commit("SET_SELECTED_MANUFACTURER", null);
    commit("SET_MANUFACTURERS", []);

    axios
      .get(`/quickbooks/resource`, {
        params: {
          locale: state.selectedLocaleId,
          resource: "Vendor",
        },
      })
      .then((res) => {
        commit("SET_LAST_LOCALE", state.selectedLocaleId);
        commit("SET_MANUFACTURERS", res.data);
      })
      .catch((e) => {
        const { errors } = state;
        errors.push("Could not retrieve available manufacturers.");
        commit("SET_ERRORS", errors);
        swal.fire({
          type: "error",
          title: "Could not retrieve QBO Vendors",
          text:
            "Visit the QuickBooks Administration panel and ensure a valid access token exists for the selected locale. If a token is present and this issue persists, please contact engineering.",
        });
      })
      .finally(() => {
        commit("SET_LOADING_MANUFACTURERS", false);
      });
  },

  SUBMIT_MANUFACTURER({ commit, getters }) {
    const payload = {
      manufacturer: getters.getManufacturerPayload,
    };

    const missingFields = Object.values(payload)
      .flatMap((obj) => Object.entries(obj).filter((e) => e[1] === null))
      .flatMap((res) => res[0]);

    if (missingFields.length > 0) {
      const err = [];
      missingFields.forEach((field) => {
        const fieldName = field
          .toLowerCase()
          .split("_")
          .map((s) => s[0].toUpperCase() + s.slice(1))
          .join(" ");

        err.push(`The ${fieldName} field is required.`);
      });
      commit("SET_ERRORS", err);
      return;
    }

    commit("SET_SUBMITTING", true);

    axios
      .post("/account/manufacturers", payload)
      .then((res) => {
        if (res.status === 201) {
          window.location = res.data;
        } else {
          throw new Error("Something unexpected happened...");
        }
      })
      .catch((e) => {
        if (e.response.data.errors) {
          commit(
            "SET_ERRORS",
            Object.values(e.response.data.errors).flatMap((value) => value)
          );
        } else {
          swal.fire({
            type: "error",
            text: "Something unexpected happened...",
          });
        }
      })
      .finally(() => {
        commit("SET_SUBMITTING", false);
      });
  },
};

const mutations = {
  SET_ERRORS(state, value) {
    state.errors = value;
  },

  SET_LOCALES(state, value) {
    state.locales = value;
  },

  SET_LOCALE(state, value) {
    state.selectedLocaleId = value;
  },

  SET_LAST_LOCALE(state, value) {
    state.lastSelectedLocale = value;
  },

  SET_LOADING_MANUFACTURERS(state, value) {
    state.loadingManufacturers = value;
  },

  SET_MANUFACTURERS(state, value) {
    state.manufacturers = value;
  },

  SET_SELECTED_MANUFACTURER(state, value) {
    state.selectedManufacturer = value;
  },

  SET_SALES_CONTACT_NAME(state, value) {
    state.salesContactName = value;
  },

  SET_SALES_CONTACT_EMAIL(state, value) {
    state.salesContactEmail = value;
  },

  SET_SALES_CONTACT_PHONE(state, value) {
    state.salesContactPhone = value;
  },

  SET_SUBMITTING(state, value) {
    state.submitting = value;
  },

  SET_TERMS(state, { localeId, value }) {
    state.terms[localeId] = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
